// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import React, { createContext, useContext, useReducer } from 'react'
import type {
    AdPlatform,
    MetaAd,
    GetAICreateSessionResponse,
} from '@storefronts/common-types'

// ── Types ──────────────────────────────────────────────────────────────────

export interface PreviewData {
    finalHash: string
    imageCurationComplete?: boolean
    /** Change = the page was rewritten; the preview iframe reloads on it. */
    refreshToken?: string
    refreshLabel?: string
}

export interface PrefaceContext {
    channel?: AdPlatform
    userInput?: string
    personaName?: string
    productNames?: string[]
    referenceUrl?: string
    sourceStorefrontName?: string
    variantCount?: number
    isManualCreative: boolean
}

export interface AIChatState {
    sessionId: string | null
    selectedAd: MetaAd | null
    prefaceContext: PrefaceContext | null
    snapshot: GetAICreateSessionResponse | null
    isComplete: boolean
    error: string | null
}

// ── Actions ────────────────────────────────────────────────────────────────

export type AIChatAction =
    | { type: 'SET_SESSION_ID'; sessionId: string }
    | { type: 'SET_AD'; ad: MetaAd }
    | { type: 'SET_PREFACE'; prefaceContext: PrefaceContext }
    | { type: 'SET_SNAPSHOT'; snapshot: GetAICreateSessionResponse }
    | { type: 'SET_ERROR'; error: string }
    | { type: 'CLEAR_ERROR' }
    | { type: 'SET_COMPLETE' }

// ── Reducer ────────────────────────────────────────────────────────────────

function chatReducer(state: AIChatState, action: AIChatAction): AIChatState {
    switch (action.type) {
        case 'SET_SESSION_ID':
            return { ...state, sessionId: action.sessionId }

        case 'SET_AD':
            return { ...state, selectedAd: action.ad }

        case 'SET_PREFACE':
            return { ...state, prefaceContext: action.prefaceContext }

        case 'SET_SNAPSHOT':
            return { ...state, snapshot: action.snapshot }

        case 'SET_ERROR':
            return { ...state, error: action.error }

        case 'CLEAR_ERROR':
            return { ...state, error: null }

        case 'SET_COMPLETE':
            return { ...state, isComplete: true }

        default:
            return state
    }
}

// ── Initial state ──────────────────────────────────────────────────────────

const initialState: AIChatState = {
    sessionId: null,
    selectedAd: null,
    prefaceContext: null,
    snapshot: null,
    isComplete: false,
    error: null,
}

// ── Context ────────────────────────────────────────────────────────────────

const AIChatContext = createContext<{
    state: AIChatState
    dispatch: React.Dispatch<AIChatAction>
} | null>(null)

export function AIChatProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(chatReducer, initialState)
    return (
        <AIChatContext.Provider value={{ state, dispatch }}>
            {children}
        </AIChatContext.Provider>
    )
}

export function useAIChatContext() {
    const ctx = useContext(AIChatContext)
    if (!ctx)
        throw new Error('useAIChatContext must be used within AIChatProvider')
    return ctx
}
